import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../../utils/api';
import { STATUS } from '../../../utils/status';
import { GetPayload, OutputJsonState } from '../../interfaces/interface';
import extraReducers from '../../extraReducers/outputJsonExtraReducer';

const initialState: OutputJsonState = {
  data: [],
  getTagsViaPlatformStatus: STATUS.NOT_STARTED,
  getTagsViaPlatformMessage: "",
  getTagsViaExcelStatus: STATUS.NOT_STARTED,
  getTagsViaExcelMessage: "",
  getSavedOutputJsonStatus: STATUS.NOT_STARTED,
  getSavedOutputJsonMessage: "",
  uploadTaskNamesStatus: STATUS.NOT_STARTED,
  uploadTaskNamesMessage: "",
  uploadCompletedTaskStatus: STATUS.NOT_STARTED,
  uploadCompletedTaskMessage: "",
  saveOutputJsonStatus: STATUS.NOT_STARTED,
  saveOutputJsonMessage: "",
  tagsPlatform: {},
  tagsExcel: {},
  getTaskStatusOutputJson: STATUS.NOT_STARTED,
  getTaskStatusOutputJsonMessage: "",
  saveoutputJsonResponse: {},
  taskStatusOutputJsonResponse: {},
};

export const getTagsViaPlatform = createAsyncThunk(
  'project/company/project/output-json/tags/patch',
  async (payload: GetPayload = {}, thunkAPI) => {
    try {
      const { project_id } = payload;
      const queryString = new URLSearchParams(
        {
          ...(project_id && { project_id }),
        }
      ).toString();

      const response = await api.get(`project/company/project/output-json/tags?${queryString}`);
      const { status, data } = response;
      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        let errorMessage = `Request failed with status ${status}`;
        if (data && data.message) {
          errorMessage += `: ${data.message}`;
          console.log({ errorMessage })
        }
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

export const getTagsViaExcel = createAsyncThunk(
  'project/company/excel/patch',
  async (payload: { id: string, file: File }, thunkAPI) => {
    const { id, file } = payload;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('id', id);
      formData.append('action', 'get_json_tags');
      const response = await api.patch(`project/company/`, formData);

      return response.data;
    } catch (error: any) {
      console.log({ error });
      if (error.response.status === 500) {
        return thunkAPI.rejectWithValue({ message: 'Wrong Format, please download template to upload correct format.' });
      }
      return thunkAPI.rejectWithValue({ message: error.response.data.message });
    }
  }
);

export const getSavedOutputJson = createAsyncThunk(
  'project/companyproject/company/project/output-json/get_schema/get',
  async (payload: GetPayload = {}, thunkAPI) => {
    try {
      const { project_id } = payload;
      const queryString = new URLSearchParams(
        {
          ...(project_id && { project_id }),
        }
      ).toString();

      const response = await api.get(`project/company/project/output-json/get_schema?${queryString}`);
      const { status, data } = response;
      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        let errorMessage = `Request failed with status ${status}`;
        if (data && data.message) {
          errorMessage += `: ${data.message}`;
          console.log({ errorMessage })
        }
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

export const getOutputJsonTaskStatus = createAsyncThunk(
  'project/company/project/output-json/tasks/status/get',
  async (payload: GetPayload = {}, thunkAPI) => {
    try {
      const { celery_task_id } = payload;
      const queryString = new URLSearchParams(
        {
          ...(celery_task_id && { celery_task_id }),
        }
      ).toString();

      const response = await api.get(`project/company/project/output-json/tasks/status?${queryString}`);
      const { status, data } = response;
      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        let errorMessage = `Request failed with status ${status}`;
        if (data && data.message) {
          errorMessage += `: ${data.message}`;
          console.log({ errorMessage })
        }
        return thunkAPI.rejectWithValue({
          ...data, 
          message: errorMessage, 
        });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

export const uploadTaskNames = createAsyncThunk(
  'project/company/taskName/PATCH',
  async (payload: { id: string, selectedFile: File }, thunkAPI) => {
    const { id, selectedFile } = payload;
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('id', id);
      formData.append('action', 'upload_tasks');
      const response = await api.patch(`project/company/`, formData);

      return response.data;
    } catch (error: any) {
      console.log({ error });
      if (error.response.status === 500) {
        return thunkAPI.rejectWithValue({ message: 'Wrong Format, please download template to upload correct format.' });
      }
      return thunkAPI.rejectWithValue({ message: error.response.data.message });
    }
  }
);

export const uploadCompletedTask = createAsyncThunk(
  'project/company/completedTask/PATCH',
  async (payload: { id: string, selectedFile: File }, thunkAPI) => {
    const { id, selectedFile } = payload;
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('id', id);
      formData.append('action', 'upload_exported_tasks_data');
      const response = await api.patch(`project/company/`, formData);

      return response.data;
    } catch (error: any) {
      console.log({ error });
      if (error.response.status === 500) {
        return thunkAPI.rejectWithValue({ message: 'Wrong Format, please download template to upload correct format.' });
      }
      return thunkAPI.rejectWithValue({ message: error.response.data.message });
    }
  }
);

export const saveOutputJson = createAsyncThunk(
  'project/company/saveOutput/PATCH',
  async (payload: { id: string, output_json: any, method: string, mapped_key: string }, thunkAPI) => {
    const { id, output_json, method, mapped_key } = payload;
    try {
      const formData = new FormData();
      formData.append('output_json_schema', output_json);
      formData.append('id', id);
      formData.append('method', method);
      formData.append('maparray_key', mapped_key);
      formData.append('action', 'save_schema');
      const response = await api.patch(`project/company/`, formData);

      return response.data;
    } catch (error: any) {
      console.log({ error });
      if (error.response.status === 500) {
        return thunkAPI.rejectWithValue({ message: 'Wrong Format, please download template to upload correct format.' });
      }
      return thunkAPI.rejectWithValue({ message: error.response.data.message });
    }
  }
);

const outputJsonSlice = createSlice({
  name: 'json',
  initialState,
  reducers: {
    setCreateSaveJsonStatus(state, action) {
      state.saveOutputJsonStatus = action.payload
    },
    setClearSaveJsonStatus(state, action) {
      state.taskStatusOutputJsonResponse = {}
    },
    setClearOutputJson(state, action) {
      state.saveoutputJsonResponse = {}
    },
    setTaskStatus(state, action) {
      state.getTaskStatusOutputJson = action.payload
    }
  },
  extraReducers
});

export const { setCreateSaveJsonStatus, setTaskStatus, setClearSaveJsonStatus, setClearOutputJson } = outputJsonSlice.actions
export default outputJsonSlice.reducer;
